"use client"

import Image from "next/image"
import { Bell, CloudRain, CloudSun, Smartphone, Sprout, ThermometerSun } from "lucide-react"

import { AppStoreBadgeLink, isAppPromoLive } from "@/components/ui/app-download-cta"
import { ChipBody, FloatingChip, Reveal } from "@/components/app/app-landing-motion"
import { PREMIUM_TRIAL_DAYS } from "@/lib/auth/feature-flags"

// Same source asset as the /app landing hero (1206x2622 App Store screenshot).
const HOME_SCREENSHOT_SRC = "/marketing/app/iphone-home.png"
const SCREENSHOT_WIDTH = 1206
const SCREENSHOT_HEIGHT = 2622

const bullets = [
  {
    icon: ThermometerSun,
    strong: "Soil-timed actions",
    rest: " for pre-emergent, green-up, and overseeding",
  },
  {
    icon: CloudSun,
    strong: "Weather-aware tasks",
    rest: " that reshape the week when rain or heat hits",
  },
  {
    icon: Bell,
    strong: "Reminders that matter",
    rest: ", only when a real lawn window opens",
  },
]

/**
 * Homepage iPhone-app section — the web-to-app bridge pitch ("the web app
 * answers today's question, the iPhone app keeps the plan with you").
 * Condensed from the /app landing hero: same phone frame, floating chips,
 * and store badge, one screen instead of five.
 *
 * Self-gates on the app-promo launch switch: renders nothing until
 * NEXT_PUBLIC_MOBILE_APP_PROMO_LIVE is true AND an App Store URL resolves,
 * matching every other web→app placement.
 */
export function HomeAppSection() {
  if (!isAppPromoLive) return null

  return (
    <section className="relative overflow-hidden bg-linear-to-b from-green-50/40 to-white text-gray-900">
      {/* Decorative color washes, matching the /app hero's blurred blobs. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -top-[90px] right-[6%] h-[320px] w-[320px] rounded-full bg-green-300/50 blur-[46px]"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-[100px] -left-[60px] h-[300px] w-[300px] rounded-full bg-amber-200/60 blur-[46px]"
      />

      <div className="relative mx-auto grid max-w-[1180px] items-center gap-12 px-6 py-20 min-[860px]:grid-cols-2">
        {/* Copy column */}
        <Reveal>
          <span className="inline-flex items-center gap-2 rounded-full border border-green-200 bg-white px-3.5 py-[7px] text-[12.5px] font-bold uppercase tracking-[0.04em] text-green-800">
            <Smartphone className="h-[15px] w-[15px] text-green-600" aria-hidden="true" />
            Now on iPhone
          </span>
          <h2 className="font-poppins mt-[18px] text-[clamp(1.9rem,3.2vw,2.6rem)] font-extrabold leading-[1.06] tracking-[-0.03em] text-gray-900">
            Your lawn plan, <span className="text-green-600">in your pocket</span> all season.
          </h2>
          <p className="mt-4 max-w-[44ch] text-[17px] leading-[1.6] text-gray-700">
            The web app answers today&apos;s question. The iPhone app keeps the plan with you:
            weather- and soil-timed tasks, one clear move each week.
          </p>

          <ul className="mt-6 flex max-w-[46ch] flex-col gap-3">
            {bullets.map(({ icon: Icon, strong, rest }) => (
              <li key={strong} className="flex items-start gap-2.5 text-[15px] leading-normal text-gray-700">
                <Icon className="mt-0.5 h-[18px] w-[18px] flex-shrink-0 text-green-600" aria-hidden="true" />
                <span>
                  <strong className="text-gray-900">{strong}</strong>
                  {rest}
                </span>
              </li>
            ))}
          </ul>

          <div className="mt-[30px] flex flex-wrap items-center gap-4">
            <AppStoreBadgeLink placement="homepage_section" />
            <span className="text-sm font-semibold text-gray-600">
              Free to download · {PREMIUM_TRIAL_DAYS} days of Premium free
            </span>
          </div>
        </Reveal>

        {/* Phone column */}
        <Reveal delay={0.12} className="relative flex justify-center">
          <div className="relative z-[2] w-[280px] max-w-[74vw] rounded-[2.75rem] border border-black bg-gray-900 p-[9px] shadow-[0_40px_80px_-24px_rgba(16,40,24,0.45),0_12px_30px_-12px_rgba(0,0,0,0.25)]">
            <Image
              src={HOME_SCREENSHOT_SRC}
              alt="What Grass Is This iOS home screen showing today's lawn task"
              width={SCREENSHOT_WIDTH}
              height={SCREENSHOT_HEIGHT}
              sizes="(max-width: 860px) 74vw, 280px"
              className="w-full rounded-[2.2rem]"
            />
          </div>
          <FloatingChip className="absolute left-0 top-16 z-[3] max-[460px]:hidden">
            <ChipBody
              tone="green"
              icon={<Sprout className="h-[19px] w-[19px]" />}
              label="Soil temp"
              value="81° · heat watch"
            />
          </FloatingChip>
          <FloatingChip delay={1.2} className="absolute bottom-[90px] right-2.5 z-[3] max-[460px]:hidden">
            <ChipBody
              tone="blue"
              icon={<CloudRain className="h-[19px] w-[19px]" />}
              label="Today"
              value="Skip watering"
            />
          </FloatingChip>
        </Reveal>
      </div>
    </section>
  )
}
